// Inspired by https://github.com/joshnuss/svelte-local-storage-store
// Note that svelte-local-storage-store also listen for storage events
// across windows, allowing to synchronize storage state between windows,
// which could come handy at some point
// Value serialization is always done in window.JSON, as it also allows to
// deserialize strings, numbers, and booleans as such

import { writable, type Writable } from 'svelte/store'
import { serverReportError } from '#app/lib/error'
import { localStorageProxy } from '#app/lib/local_storage'
import type { SubEntitiesBrowserDisplayMode } from '#entities/components/layouts/sub_entities_browser.svelte'
import type { InventoryBrowserDisplayName } from '#inventory/components/inventory_browser_controls.svelte'
import type { HistoryEntry } from '#search/lib/search_results_history'

interface LocalStorageStoresValues {
  bibliographicDataNoticeHasBeenSeen: boolean
  displayLatestUsersWithReportsOnly: boolean
  entitiesDisplay: SubEntitiesBrowserDisplayMode
  inventoryDisplay: InventoryBrowserDisplayName
  mainUserInventoryDisplay: InventoryBrowserDisplayName
  searches: HistoryEntry[]
  showBibliographicDataNotice: boolean
}

export type LocalStorageStores = {
  [T in keyof LocalStorageStoresValues]: Writable<LocalStorageStoresValues[T]>
}

type LocalStorageStoreKey = keyof LocalStorageStoresValues

const stores: Partial<LocalStorageStores> = {}

export function getLocalStorageStore <T extends LocalStorageStoreKey> (key: T, initialValue: LocalStorageStoresValues[T]) {
  // @ts-expect-error
  stores[key] ??= initStore(key, initialValue) as Writable<LocalStorageStoresValues[T]>
  return stores[key] as LocalStorageStores[T]
}

function initStore <T extends LocalStorageStoreKey> (key: T, initialValue: LocalStorageStoresValues[T]) {
  const start = set => {
    let value, stringifiedStoredValue
    try {
      stringifiedStoredValue = localStorageProxy.getItem(key)
      value = JSON.parse(stringifiedStoredValue)
    } catch (err) {
      if (err.name === 'SyntaxError') localStorageProxy.removeItem(key)
      serverReportError(err, { key, initialValue, stringifiedStoredValue })
    }
    if (value == null) value = initialValue
    set(value)
    const stop = () => delete stores[key]
    return stop
  }
  const store = writable(initialValue, start)
  return {
    set (value) {
      // There is no known case yet, where saving an empty value is the desired behavior
      // There is a known case where the store is shortly assigned an empty value:
      // when binding the store to an input, the value may start by being undefined
      if (value != null) {
        localStorageProxy.setItem(key, JSON.stringify(value))
      }
      store.set(value)
    },
    subscribe: store.subscribe,
  } as Writable<LocalStorageStoresValues[T]>
}
